import React, { useEffect, useRef, useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import './Blog.css';
import { useLanguage } from '../context/LanguageContext';
import { Link, useParams, useNavigate } from 'react-router-dom';
import blog1Image from '../assets/blog/blog1.jpg';

// Composant pour les particules - Réutilisé de Blog.js
const BlogParticles = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Ajuster la taille du canvas à la section
    const resizeCanvas = () => {
      const parentElement = canvas.parentElement;
      if (parentElement) {
        canvas.width = parentElement.offsetWidth;
        canvas.height = parentElement.offsetHeight;
      }
    };
    
    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();
    
    // Configuration des particules
    const particlesArray = [];
    const numberOfParticles = Math.min(window.innerWidth / 10, 120);
    
    const mouse = {
      x: undefined,
      y: undefined,
      radius: 100
    };
    
    // Capturer la position de la souris
    const handleMouseMove = (event) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = event.clientX - rect.left;
      mouse.y = event.clientY - rect.top;
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    
    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        
        // Taille variable pour plus de dynamisme
        this.size = Math.random() * 2.5 + 0.8;
        this.baseSize = this.size;
        
        // Vitesse et direction
        this.speedX = (Math.random() * 1 - 0.5) * 0.6;
        this.speedY = (Math.random() * 1 - 0.5) * 0.6;
        this.baseSpeedX = this.speedX;
        this.baseSpeedY = this.speedY;
        
        // Animation et apparence
        this.pulsing = Math.random() * 0.03 + 0.01;
        this.colorAngle = Math.random() * 360;
        this.colorSpeed = Math.random() * 0.3 + 0.1;
        this.alpha = Math.random() * 0.4 + 0.3;
        this.glow = Math.random() * 6 + 2;
        this.hueOffset = Math.random() * 20 - 10;
      }
      
      update() {
        // Mouvement de base
        this.x += this.speedX;
        this.y += this.speedY;
        
        // Animation pulsante et changement de couleur
        this.colorAngle += this.colorSpeed;
        if (this.colorAngle > 360) this.colorAngle = 0;
        
        // Effet respirant
        this.size = this.baseSize + Math.sin(Date.now() * this.pulsing) * (this.baseSize * 0.3);
        
        // Interaction avec la souris
        if (mouse.x) {
          const mdx = mouse.x - this.x;
          const mdy = mouse.y - this.y;
          const mouseDistance = Math.sqrt(mdx * mdx + mdy * mdy);
          
          if (mouseDistance < mouse.radius) {
            const angle = Math.atan2(mdy, mdx);
            const force = (mouse.radius - mouseDistance) / mouse.radius;
            
            // Effet de répulsion
            this.speedX -= Math.cos(angle) * force * 0.2;
            this.speedY -= Math.sin(angle) * force * 0.2;
            
            // Augmenter l'opacité et la taille à l'approche de la souris
            this.alpha = Math.min(this.alpha + 0.2, 0.9);
            this.size = this.baseSize * (1 + force * 0.9);
          } else {
            // Revenir progressivement à la vitesse et opacité de base
            this.speedX = this.speedX * 0.97 + this.baseSpeedX * 0.03;
            this.speedY = this.speedY * 0.97 + this.baseSpeedY * 0.03;
            this.alpha = Math.max(this.alpha * 0.98, Math.random() * 0.4 + 0.3);
            this.size = Math.max(this.size * 0.98, this.baseSize);
          }
        }
        
        // Limiter la vitesse
        const maxSpeed = 1.5;
        const currentSpeed = Math.sqrt(this.speedX * this.speedX + this.speedY * this.speedY);
        if (currentSpeed > maxSpeed) {
          this.speedX = (this.speedX / currentSpeed) * maxSpeed;
          this.speedY = (this.speedY / currentSpeed) * maxSpeed;
        }
        
        // Friction légère
        this.speedX *= 0.99;
        this.speedY *= 0.99;
        
        // Rebond aux limites de l'écran
        const margin = 10; // Marge pour éviter que les particules ne disparaissent complètement
        
        if (this.x < margin) {
          this.x = margin;
          this.speedX *= -0.8;
        } else if (this.x > canvas.width - margin) {
          this.x = canvas.width - margin;
          this.speedX *= -0.8;
        }
        
        if (this.y < margin) {
          this.y = margin;
          this.speedY *= -0.8;
        } else if (this.y > canvas.height - margin) {
          this.y = canvas.height - margin;
          this.speedY *= -0.8;
        }
      }
      
      draw() {
        // Centre plus défini avec moins de halo
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size * 0.7, 0, Math.PI * 2);
        
        // Couleur magenta comme l'image
        const hue = Math.abs(Math.sin(this.colorAngle) * 30) + 320 + this.hueOffset;
        const saturation = 100;
        const lightness = 68 + Math.sin(this.colorAngle * 0.5) * 8;
        
        // Centre plus opaque pour mieux définir la particule
        ctx.fillStyle = `hsla(${hue}, ${saturation}%, ${lightness}%, ${this.alpha * 1.2})`;
        ctx.fill();
        
        // Effet de halo lumineux
        const gradient = ctx.createRadialGradient(
          this.x, this.y, 0,
          this.x, this.y, this.size + this.glow
        );
        
        gradient.addColorStop(0, `hsla(${hue}, ${saturation}%, ${lightness + 10}%, ${this.alpha * 0.9})`);
        gradient.addColorStop(0.3, `hsla(${hue}, ${saturation}%, ${lightness}%, ${this.alpha * 0.6})`);
        gradient.addColorStop(0.6, `hsla(${hue}, ${saturation}%, ${lightness - 10}%, ${this.alpha * 0.2})`);
        gradient.addColorStop(1, `hsla(${hue}, ${saturation}%, ${lightness - 20}%, 0)`);
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size + this.glow, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    
    // Créer les particules
    const init = () => {
      particlesArray.length = 0;
      for (let i = 0; i < numberOfParticles; i++) {
        particlesArray.push(new Particle());
      }
    };
    init();
    
    // Animer les particules
    let animationFrameId;
    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particlesArray.forEach(particle => {
        particle.update();
        particle.draw();
      });
      
      animationFrameId = requestAnimationFrame(animate);
    }
    animate();
    
    // Nettoyage
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);
  
  return (
    <canvas ref={canvasRef} className="blog-particles" />
  );
};

// Composant pour les icônes des réseaux sociaux
const getSocialIconSvg = (network) => {
  switch(network) {
    case 'x':
      return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M23 3.01C22.0424 3.6759 20.9821 4.1912 19.86 4.53C19.2577 3.83749 18.4573 3.34669 17.567 3.12391C16.6767 2.90115 15.7395 2.9572 14.8821 3.28445C14.0247 3.61168 13.2884 4.19461 12.773 4.95367C12.2575 5.71275 11.9877 6.61357 12 7.53V8.53C10.2426 8.57557 8.50127 8.1858 6.93101 7.39571C5.36074 6.60563 4.01032 5.43868 3 4C3 4 -1 13 8 17C5.94053 18.398 3.48716 19.0989 1 19C10 24 21 19 21 7.5C20.9991 7.22145 20.9723 6.94359 20.92 6.67C21.9406 5.66349 22.6608 4.39271 23 3.01Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
    case 'instagram':
      return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="2" width="20" height="20" rx="5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M16 11.37C16.1234 12.2022 15.9813 13.0522 15.5938 13.799C15.2063 14.5458 14.5932 15.1514 13.8416 15.5297C13.0901 15.9079 12.2385 16.0396 11.4078 15.9059C10.5771 15.7723 9.80977 15.3801 9.21485 14.7852C8.61993 14.1902 8.22774 13.4229 8.09408 12.5922C7.96042 11.7615 8.09208 10.9099 8.47034 10.1584C8.8486 9.40685 9.4542 8.79374 10.201 8.40624C10.9478 8.01874 11.7978 7.87659 12.63 8C13.4789 8.12588 14.2649 8.52146 14.8717 9.12831C15.4785 9.73515 15.8741 10.5211 16 11.37Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <circle cx="16.5" cy="7.5" r="1.5" fill="currentColor"/>
      </svg>`;
    case 'tiktok':
      return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9 12C6.79086 12 5 13.7909 5 16C5 18.2091 6.79086 20 9 20C11.2091 20 13 18.2091 13 16V4C13.7286 4 14.2286 4.38857 15 5C15.8286 5.66571 16.5 7 16.5 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M13 7C14.6364 7 16.0727 6.45455 17 5.5C17.9273 4.54545 18.5 3.27273 18.5 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
    case 'arrow-left':
      return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M19 12H5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M12 19L5 12L12 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
    default:
      return '';
  }
};

// Articles de blog disponibles
const articles = [
  {
    id: 1,
    title: "Comment organiser une soirée quiz entre amis ou en famille ?",
    date: "18 avril 2024",
    excerpt: "Découvrez comment transformer vos soirées ordinaires en moments inoubliables grâce aux quiz interactifs.",
    thumbnail: blog1Image,
    tags: ["Soirée", "Amis", "Famille", "Divertissement"],
    metaTitle: "Comment organiser une soirée quiz entre amis ou en famille – Nos conseils avec Quizzer",
    metaDescription: "Envie d'une soirée originale et fun ? Découvrez comment organiser facilement une soirée quiz en duo avec Quizzer, l'app parfaite pour défier vos proches et créer des moments inoubliables.",
    contentHtml: `
      <p>On a tous connu ces soirées qui tournent un peu en rond. Tu sais, quand les discussions s'épuisent, que chacun commence à pianoter sur son téléphone, et qu'on sent qu'il manque un petit quelque chose pour réveiller l'ambiance.</p>

      <p>Et si ce "petit quelque chose", c'était… un quiz ?</p>

      <p>C'est simple, drôle, accessible à tous, et surtout : c'est le moyen parfait de remettre un peu de vie et d'énergie dans la pièce.</p>

      <h2>Le quiz, la recette parfaite pour une soirée réussie</h2>
      <p>Pas besoin d'être un expert de l'animation ou d'avoir préparé la soirée trois semaines à l'avance. Avec <span class="highlight-quizzer">Quizzer</span>, tu peux lancer un duel en quelques secondes. Un simple face à face : toi contre un pote, un membre de la famille, ou même ce voisin que tu connais à peine. Et là, ça décolle.</p>

      <p>8 questions, des thèmes pour tous les goûts, un chrono qui fait monter la tension, et surtout ce petit frisson quand tu vois que tu mènes (ou que tu te fais rattraper). L'ambiance monte d'elle-même. Et très vite, les autres veulent s'y mettre aussi.</p>

      <h2>Ce qui fait la magie du moment</h2>
      <p>Ce n'est pas juste un jeu de plus. Le format 1 contre 1 crée une vraie tension sympa, une complicité un peu compétitive. On se chambre, on se défie, on se promet une revanche. Et ce qui ne devait être qu'une petite partie devient souvent le cœur de la soirée.</p>

      <p>Les thèmes sont variés et bien pensés : musique, cinéma, culture générale, nostalgie des années 2000… chacun a sa chance de briller. Et ça crée des moments de rire, de surprise, et parfois même de fierté ("Tu savais ça toi ?!").</p>

      <h2>En résumé…</h2>
      <p>Envie de casser la routine et de vivre une soirée vraiment différente ?<br/>
      Lance un quiz sur <span class="highlight-quizzer">Quizzer</span>, choisis ton adversaire, et laisse la magie opérer.<br/>
      C'est rapide, c'est fun, ça met tout le monde dans le mood… et ça crée des souvenirs bien plus forts qu'une énième soirée à faire défiler son fil d'actu.</p>
    `
  }
];

function BlogArticle() {
  const { t } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [prevArticle, setPrevArticle] = useState(null);
  const [nextArticle, setNextArticle] = useState(null);
  
  // Assurer que la page s'affiche tout en haut au chargement
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Récupérer l'article correspondant à l'ID
  useEffect(() => {
    const articleId = parseInt(id);
    const currentArticle = articles.find(article => article.id === articleId);
    
    if (currentArticle) {
      setArticle(currentArticle);
      
      // Trouver les articles précédent et suivant pour la navigation
      const currentIndex = articles.findIndex(article => article.id === articleId);
      if (currentIndex > 0) {
        setPrevArticle(articles[currentIndex - 1]);
      } else {
        setPrevArticle(null);
      }
      
      if (currentIndex < articles.length - 1) {
        setNextArticle(articles[currentIndex + 1]);
      } else {
        setNextArticle(null);
      }
    } else {
      // Rediriger vers la page de blog si l'article n'existe pas
      navigate('/blog');
    }
  }, [id, navigate]);

  // Utiliser useEffect pour modifier directement le titre et la meta description
  useEffect(() => {
    if (article) {
      // Utiliser des valeurs fixes hardcodées pour éviter tout problème
      const pageTitle = "Article de Blog - Quizzer";
      const pageDescription = "Découvrez nos contenus exclusifs sur Quizzer, l'application de quiz";
      
      // Mettre à jour le titre de la page
      document.title = pageTitle;
      
      // Mettre à jour la meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', pageDescription);
      } else {
        const meta = document.createElement('meta');
        meta.name = 'description';
        meta.content = pageDescription;
        document.head.appendChild(meta);
      }
      
      // Nettoyage si nécessaire
      return () => {
        // Optionnel: remettre les valeurs par défaut
      };
    }
  }, [article]);

  // Si l'article n'est pas encore chargé, affichez un chargement ou retournez null
  if (!article) {
    return null;
  }

  return (
    <div className="blog-page">
      <Header />
      <main className="main-content">
        <section className="blog-section">
          <BlogParticles />
          <div className="blog-container fade-in">
            <div className="blog-article">
              <Link to="/blog" className="back-button">
                <div dangerouslySetInnerHTML={{ __html: getSocialIconSvg('arrow-left') }} />
                {t('backToArticles')}
              </Link>
              
              <div className="article-header">
                <div className="article-thumbnail">
                  <img src={article.thumbnail} alt={article.title} />
                </div>
                <div className="article-meta">
                  <span className="article-date">{article.date}</span>
                  <h2 className="article-title">{article.title}</h2>
                  <div className="article-tags">
                    {article.tags.map((tag, index) => (
                      <span key={index} className="article-tag">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="article-content" dangerouslySetInnerHTML={{ __html: article.contentHtml }} />

              <div className="article-navigation">
                {prevArticle && (
                  <Link to={`/blog/${prevArticle.id}`} className="article-nav-btn prev">
                    <span>{prevArticle.title}</span>
                  </Link>
                )}
                
                {nextArticle && (
                  <Link to={`/blog/${nextArticle.id}`} className="article-nav-btn next">
                    <span>{nextArticle.title}</span>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default BlogArticle; 